export interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
}

export const updatePageSEO = (options: SEOConfig) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ngym-studyarchive.com';
  const {
    title = '永山の勉強アーカイブ｜学習記録・勉強日記・クイズ・講義動画',
    description = '永山の勉強アーカイブは、日々の学習内容を記録・整理する個人学習サイトです。勉強日記、学習資料、講義動画、一問一答クイズで効率的に復習できます。',
    keywords = '永山の勉強アーカイブ,勉強アーカイブ,学習記録,勉強日記',
    canonical = `${siteUrl}/`,
    ogType = 'website',
    ogImage = 'https://public.readdy.ai/ai/img_res/d86da662-714b-468b-918d-1f8f123bb493.png',
  } = options;

  // Update title
  document.title = title;

  // Update or create meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute('content', description);

  // Update or create meta keywords
  if (keywords) {
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', keywords);
  }

  // Update canonical URL
  if (canonical) {
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = canonical;
  }

  // Update OG tags
  const updateOGTag = (property: string, content: string) => {
    let tag = document.querySelector(`meta[property="${property}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('property', property);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  };

  updateOGTag('og:title', title);
  updateOGTag('og:description', description);
  if (canonical) {
    updateOGTag('og:url', canonical);
  }
  if (ogType) {
    updateOGTag('og:type', ogType);
  }

  // Update Twitter Card
  const updateTwitterTag = (name: string, content: string) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', name);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  };

  updateTwitterTag('twitter:title', title);
  updateTwitterTag('twitter:description', description);
};

export const createBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  let scriptTag = document.querySelector('script[data-schema="breadcrumb"]');
  if (!scriptTag) {
    scriptTag = document.createElement('script');
    scriptTag.setAttribute('type', 'application/ld+json');
    scriptTag.setAttribute('data-schema', 'breadcrumb');
    document.head.appendChild(scriptTag);
  }
  scriptTag.textContent = JSON.stringify(schema);
};

const BASE_URL = import.meta.env.VITE_SITE_URL || 'https://ngym-studyarchive.com';
const SITE_NAME = '\u6c38\u5c71\u306e\u52c9\u5f37\u30a2\u30fc\u30ab\u30a4\u30d6';
const SITE_LOGO = 'https://public.readdy.ai/ai/img_res/d86da662-714b-468b-918d-1f8f123bb493.png';

const upsertSchema = (id: string, data: Record<string, unknown>) => {
  let scriptTag = document.querySelector(`script[data-schema="${id}"]`);
  if (!scriptTag) {
    scriptTag = document.createElement('script');
    scriptTag.setAttribute('type', 'application/ld+json');
    scriptTag.setAttribute('data-schema', id);
    document.head.appendChild(scriptTag);
  }
  scriptTag.textContent = JSON.stringify(data);
};

export const removeSchema = (id: string) => {
  const tag = document.querySelector(`script[data-schema="${id}"]`);
  if (tag) tag.remove();
};

export const cleanupDynamicSchemas = () => {
  const ids = ['page-schema', 'collection-schema', 'faq-schema', 'educational-schema', 'video-schema'];
  ids.forEach(removeSchema);
};

export interface PageSchemaConfig {
  name: string;
  description: string;
  url: string;
  type?: string;
  dateModified?: string;
}

export const createPageSchema = (config: PageSchemaConfig) => {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': config.type || 'WebPage',
    name: config.name,
    description: config.description,
    url: config.url,
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: BASE_URL + '/',
    },
    publisher: {
      '@type': 'Person',
      name: '\u6c38\u5c71',
    },
    inLanguage: 'ja',
    dateModified: config.dateModified || new Date().toISOString().split('T')[0],
  };
  upsertSchema('page-schema', schema);
};

export interface CollectionPageSchemaConfig {
  name: string;
  description: string;
  url: string;
  items?: Array<{ name: string; description?: string; url?: string }>;
}

export const createCollectionPageSchema = (config: CollectionPageSchemaConfig) => {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: config.name,
    description: config.description,
    url: config.url,
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: BASE_URL + '/',
    },
    publisher: {
      '@type': 'Person',
      name: '\u6c38\u5c71',
    },
    inLanguage: 'ja',
  };

  if (config.items && config.items.length > 0) {
    schema.mainEntity = {
      '@type': 'ItemList',
      itemListElement: config.items.slice(0, 20).map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        ...(item.description ? { description: item.description } : {}),
        ...(item.url ? { url: item.url } : {}),
      })),
    };
  }

  upsertSchema('collection-schema', schema);
};

export interface FAQItem {
  question: string;
  answer: string;
}

export const createFAQPageSchema = (faqs: FAQItem[]) => {
  if (faqs.length === 0) return;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
  upsertSchema('faq-schema', schema);
};

export interface EducationalContentConfig {
  name: string;
  description: string;
  url: string;
  subjects?: string[];
  educationalLevel?: string;
}

export const createEducationalContentSchema = (config: EducationalContentConfig) => {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: config.name,
    description: config.description,
    url: config.url,
    provider: {
      '@type': 'Person',
      name: '\u6c38\u5c71',
    },
    inLanguage: 'ja',
    isAccessibleForFree: true,
  };

  if (config.subjects && config.subjects.length > 0) {
    schema.about = config.subjects.map((s) => ({
      '@type': 'Thing',
      name: s,
    }));
  }

  if (config.educationalLevel) {
    schema.educationalLevel = config.educationalLevel;
  }

  upsertSchema('educational-schema', schema);
};

export interface VideoSchemaConfig {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  contentUrl?: string;
  embedUrl?: string;
  duration?: string;
}

export const createVideoListSchema = (videos: VideoSchemaConfig[]) => {
  if (videos.length === 0) return;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: videos.slice(0, 20).map((video, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'VideoObject',
        name: video.name,
        description: video.description || video.name,
        thumbnailUrl: video.thumbnailUrl,
        uploadDate: video.uploadDate,
        ...(video.contentUrl ? { contentUrl: video.contentUrl } : {}),
        ...(video.embedUrl ? { embedUrl: video.embedUrl } : {}),
        ...(video.duration ? { duration: video.duration } : {}),
      },
    })),
  };
  upsertSchema('video-schema', schema);
};
